import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Brain, MessageCircle, ClipboardList, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";
import { useScrollFadeIn, fadeCls, fadeStyle } from "@/hooks/useScrollFadeIn";

const STEPS = [
  { key: "map", icon: ClipboardList },
  { key: "understand", icon: Brain },
  { key: "use", icon: MessageCircle },
  { key: "learn", icon: TrendingUp },
] as const;

function MockMap() {
  const { t } = useTranslation("ui");
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {t("relark.howItWorks.mocks.mapTitle")}
      </p>
      <p className="mt-2 text-sm font-medium">{t("relark.howItWorks.mocks.mapQuestion")}</p>
      <div className="mt-3 space-y-1.5">
        {[80, 60, 40].map((w, i) => (
          <div key={i} className="h-2 w-full rounded-full bg-muted">
            <div className="h-full rounded-full bg-primary/60" style={{ width: `${w}%` }} />
          </div>
        ))}
      </div>
      <p className="mt-3 text-[10px] text-muted-foreground">
        {t("relark.howItWorks.mocks.mapProgress")}
      </p>
    </div>
  );
}

export function MockUnderstand() {
  const { t } = useTranslation("ui");
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
          <Brain className="h-4 w-4 text-primary" />
        </div>
        <span className="rounded-full border border-primary/30 bg-primary/5 px-2 py-0.5 text-[10px] font-semibold text-primary uppercase tracking-wider">
          {t("relark.howItWorks.mocks.understandBadge")}
        </span>
      </div>
      <p className="mt-3 text-sm font-bold text-primary">
        {t("relark.howItWorks.mocks.understandArchetype")}
      </p>
      <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
        {t("relark.howItWorks.mocks.understandDesc")}
      </p>
    </div>
  );
}

function MockUse() {
  const { t } = useTranslation("ui");
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
      <div className="flex items-center gap-2 border-b border-border pb-2 mb-3">
        <div className="flex h-6 w-6 items-center justify-center rounded bg-primary/10">
          <MessageCircle className="h-3 w-3 text-primary" />
        </div>
        <span className="text-[11px] font-medium text-muted-foreground">
          #relark
        </span>
      </div>
      <p className="text-xs leading-relaxed">
        <span className="font-semibold text-primary">{t("relark.howItWorks.mocks.useLabel")}: </span>
        "{t("relark.howItWorks.mocks.useScript")}"
      </p>
    </div>
  );
}

function MockLearn() {
  const { t } = useTranslation("ui");
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded bg-primary/10">
            <TrendingUp className="h-3 w-3 text-primary" />
          </div>
          <span className="text-[11px] font-medium text-muted-foreground">
            {t("relark.howItWorks.mocks.learnTitle")}
          </span>
        </div>
      </div>
      <p className="text-xs font-medium mb-2">{t("relark.howItWorks.mocks.learnQuestion")}</p>
      <div className="flex flex-wrap gap-1.5 mb-3">
        <span className="rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[10px] font-medium">
          {t("relark.howItWorks.mocks.learnRatingGood")}
        </span>
        <span className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10px] text-muted-foreground">
          {t("relark.howItWorks.mocks.learnRatingMixed")}
        </span>
        <span className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10px] text-muted-foreground">
          {t("relark.howItWorks.mocks.learnRatingBad")}
        </span>
      </div>
      <p className="text-[10px] leading-relaxed text-muted-foreground italic border-t border-border pt-2">
        {t("relark.howItWorks.mocks.learnDriftNote")}
      </p>
    </div>
  );
}

function MockBySlot(slot: string) {
  if (slot === "map") return <MockMap />;
  if (slot === "understand") return <MockUnderstand />;
  if (slot === "learn") return <MockLearn />;
  return <MockUse />;
}

export default function RelarkHowItWorks() {
  const { t } = useTranslation("ui");
  const navigate = useNavigate();
  const { ref, isVisible } = useScrollFadeIn(0.1);

  return (
    <section id="how" className="py-16 sm:py-24">
      <div ref={ref} className="mx-auto max-w-5xl px-5">
        <div className={`text-center max-w-2xl mx-auto ${fadeCls(isVisible)}`}>
          <h2 className="text-2xl font-bold sm:text-3xl">{t("relark.howItWorks.title")}</h2>
          <p className="mt-3 text-muted-foreground">{t("relark.howItWorks.sub")}</p>
        </div>

        <div className="relative mt-12 space-y-10 lg:space-y-14">
          {/* vertical line behind circles (desktop) */}
          <div className="pointer-events-none absolute left-1/2 top-0 hidden h-full w-px -translate-x-1/2 bg-border lg:block" />

          {STEPS.map(({ key, icon: Icon }, idx) => {
            const reversed = idx % 2 === 1;
            return (
              <div
                key={key}
                className={`relative grid grid-cols-1 items-center gap-6 lg:grid-cols-[1fr_auto_1fr] ${fadeCls(isVisible)}`}
                style={fadeStyle(idx * 200)}
              >
                {/* TEXT */}
                <div className={reversed ? "lg:order-3 lg:text-left" : "lg:text-right"}>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-primary/70">
                    {t(`relark.howItWorks.steps.${key}.eyebrow`)}
                  </p>
                  <h3 className="mt-2 text-xl font-bold">
                    {t(`relark.howItWorks.steps.${key}.title`)}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {t(`relark.howItWorks.steps.${key}.body`)}
                  </p>
                  <p className={`mt-3 text-sm font-medium text-primary/90 leading-relaxed ${reversed ? "" : "lg:text-right"}`}>
                    → {t(`relark.howItWorks.steps.${key}.outcome`)}
                  </p>
                </div>

                {/* NUMBER CIRCLE */}
                <div className="relative z-10 mx-auto lg:order-2 flex h-14 w-14 items-center justify-center rounded-full border-2 border-primary bg-background shadow-md">
                  <Icon className="h-5 w-5 text-primary" />
                </div>

                {/* MOCKUP */}
                <div className={reversed ? "lg:order-1" : "lg:order-3"}>
                  {MockBySlot(key)}
                </div>
              </div>
            );
          })}
        </div>

        <div
          className={`mt-10 flex justify-center ${fadeCls(isVisible)}`}
          style={fadeStyle(700)}
        >
          <span className="rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5 text-xs font-medium text-primary">
            {t("relark.howItWorks.preMortemBadge")}
          </span>
        </div>

        <div
          className={`mt-8 flex flex-col items-center gap-3 ${fadeCls(isVisible)}`}
          style={fadeStyle(800)}
        >
          <Button
            size="lg"
            className="h-auto min-h-[52px] rounded-full px-8 py-3.5 text-base font-semibold shadow-md shadow-primary/20"
            onClick={() => {
              trackEvent("relark_cta_click", { label: "how_it_works" });
              navigate("/onboarding");
            }}
          >
            {t("relark.howItWorks.cta")}
            <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
          <span className="rounded-full bg-muted/60 px-3 py-1 text-xs text-muted-foreground">
            {t("relark.howItWorks.noCard")}
          </span>
          <Link
            to="/science"
            className="text-xs text-muted-foreground hover:text-primary transition-colors underline-offset-4 hover:underline"
            onClick={() => trackEvent("science_link_click", { from: "how_it_works" })}
          >
            {t("relark.scienceLink.readMethod")}
          </Link>
        </div>
      </div>
    </section>
  );
}